import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Contact = () => {
  const { t } = useLanguage();
  const [scrollY, setScrollY] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: 'pentest',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSuccessMessage, setIsSuccessMessage] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    setIsSuccessMessage(false);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setIsSuccessMessage(true);
        setSubmitMessage(t.contact.form.success);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: 'pentest',
          message: ''
        });
      } else {
        setIsSuccessMessage(false);
        const errMsg = Array.isArray(data.detail) ? data.detail.map((e) => e.msg || e.message).join(' ') : data.detail;
        setSubmitMessage(errMsg || t.contact.form.error);
      }
    } catch (error) {
      setIsSuccessMessage(false);
      setSubmitMessage(t.contact.form.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero com Parallax */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${scrollY * 0.4}px)`,
            willChange: 'transform'
          }}
        />
        <div className="absolute inset-0 opacity-10 bg-[linear-gradient(rgba(11,112,42,0.2)_1px,transparent_1px),linear-gradient(90deg,rgba(11,112,42,0.2)_1px,transparent_1px)] bg-[size:40px_40px]" style={{ transform: `translateY(${scrollY * 0.2}px)`, willChange: 'transform' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#014421]/40 via-[#014421]/20 to-black" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">{t.contact.title}</h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-4xl font-bold mb-8">{t.contact.talkToUs}</h2>
              <p className="text-xl text-gray-300 mb-12 leading-relaxed">
                {t.contact.talkToUsText}
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-7 h-7 text-[#0B702A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{t.contact.email}</h3>
                    <p className="text-gray-400">software.secure.adm@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-7 h-7 text-[#0B702A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{t.contact.phone}</h3>
                    <p className="text-gray-400">(11) 96840-2230</p>
                    <p className="text-gray-400">Seg - Sex: 9h às 18h</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-7 h-7 text-[#0B702A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{t.contact.location}</h3>
                    <p className="text-gray-400">São Paulo, SP</p>
                    <p className="text-gray-400">Brasil</p>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="mt-12 p-6 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl">
                <h3 className="text-2xl font-bold mb-4">{t.contact.businessHours}</h3>
                <div className="space-y-2 text-gray-300">
                  <div className="flex justify-between">
                    <span>{t.contact.monday}</span>
                    <span className="text-[#0B702A]">{t.contact.mondayHours}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t.contact.saturday}</span>
                    <span className="text-[#0B702A]">{t.contact.saturdayHours}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t.contact.sunday}</span>
                    <span className="text-gray-500">{t.contact.closed}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl p-8">
              <h2 className="text-3xl font-bold mb-6">{t.contact.sendMessage}</h2>

              {submitMessage && (
                <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${isSuccessMessage
                  ? 'bg-[#0B702A]/20 border border-[#0B702A]/40'
                  : 'bg-red-500/20 border border-red-500/40'
                  }`}>
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <p className="text-sm">{isSuccessMessage ? t.contact.form.success : submitMessage}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2">
                    {t.contact.form.name} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-[#0B702A]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0B702A] transition-colors"
                    placeholder={t.contact.form.namePlaceholder}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2">
                    {t.contact.form.email} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-[#0B702A]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0B702A] transition-colors"
                    placeholder={t.contact.form.emailPlaceholder}
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium mb-2">
                    {t.contact.form.phone}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-black/50 border border-[#0B702A]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0B702A] transition-colors"
                    placeholder={t.contact.form.phonePlaceholder}
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium mb-2">
                    {t.contact.form.company}
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-black/50 border border-[#0B702A]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0B702A] transition-colors"
                    placeholder={t.contact.form.companyPlaceholder}
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium mb-2">
                    {t.contact.form.service} *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-[#0B702A]/30 rounded-lg text-white focus:outline-none focus:border-[#0B702A] transition-colors"
                  >
                    <option value="pentest">{t.contact.form.servicePentest}</option>
                    <option value="desenvolvimento">{t.contact.form.serviceDevelopment}</option>
                    <option value="auditoria">{t.contact.form.serviceAudit}</option>
                    <option value="consultoria">{t.contact.form.serviceConsulting}</option>
                    <option value="outro">{t.contact.form.serviceOther}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-2">
                    {t.contact.form.message} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-black/50 border border-[#0B702A]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0B702A] transition-colors resize-none"
                    placeholder={t.contact.form.messagePlaceholder}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-[#0B702A] text-white font-semibold rounded-lg hover:bg-[#0C9F39] transition-all duration-300 shadow-lg hover:shadow-[#0B702A]/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t.contact.form.submitting : t.contact.form.submit}
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;